// MiReporte — staff submits their structured ventas/propinas for an event
// (Sophi App v2 Piece 5). Replaces the old `venta X propina Y` regex flow
// in the Telegram event group. Both surfaces are live in parallel; the
// server's already-reported guard returns 409 with the existing values so
// this UI can offer a "¿Reemplazar?" confirmation before retrying.
//
// POS photo upload is reserved for a follow-up slice — the `ss-evento-pos`
// bucket is already provisioned. Form is launchable from EventDetailModal.

import { useEffect, useState } from 'preact/hooks';
import {
  getClosureStatus, submitMyReport, ApiError,
  type ClosureStatus, type MyReportSuccess,
} from '../lib/api';
import { haptic } from '../lib/telegram';

interface MiReporteProps {
  eventId: string;
  eventTitle?: string;
  onClose: () => void;
}

type Stage = 'loading' | 'ready' | 'submitting' | 'conflict' | 'done' | 'error';

interface ConflictState {
  existing: { ventas: number; propinas: number; source: 'app' | 'group'; edited: boolean };
  attempted: { ventas: number; propinas: number };
}

export function MiReporte({ eventId, eventTitle, onClose }: MiReporteProps) {
  const [stage, setStage] = useState<Stage>('loading');
  const [status, setStatus] = useState<ClosureStatus | null>(null);
  const [ventas, setVentas] = useState('');
  const [propinas, setPropinas] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ConflictState | null>(null);
  const [result, setResult] = useState<MyReportSuccess | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await getClosureStatus(eventId);
        setStatus(s);
        if (s.myReport) {
          setVentas(String(s.myReport.ventas));
          setPropinas(String(s.myReport.propinas));
        }
        setStage('ready');
      } catch (e) {
        setError((e as Error).message);
        setStage('error');
      }
    })();
  }, [eventId]);

  async function submit(force = false) {
    const v = Number(ventas);
    const p = Number(propinas);
    if (!Number.isFinite(v) || v < 0) { setError('Ventas inválidas'); return; }
    if (!Number.isFinite(p) || p < 0) { setError('Propinas inválidas'); return; }
    setError(null);
    setStage('submitting');
    haptic('light');
    try {
      const r = await submitMyReport(eventId, { ventas: v, propinas: p, force });
      haptic('success');
      setResult(r);
      setStage('done');
    } catch (e) {
      if (e instanceof ApiError && (e.status === 409 || e.status === 403)) {
        // Conflict — existing report. Show "¿Reemplazar?" UI.
        const existing = e.body && (e.body as any).existing;
        if (existing) {
          setConflict({ existing, attempted: { ventas: v, propinas: p } });
          setStage('conflict');
          return;
        }
        // 409 without existing — server says no_state (closure not initiated)
        setError(
          e.message === 'no_state'
            ? 'El capitán aún no ha iniciado el cierre del evento.'
            : e.message === 'staff_not_in_team'
            ? 'No estás en el equipo de este evento.'
            : e.message
        );
        setStage('error');
        return;
      }
      setError((e as Error).message);
      setStage('error');
      haptic('error');
    }
  }

  if (stage === 'loading') {
    return (
      <div class="event-chat-screen">
        <Header eventTitle={eventTitle} eventId={eventId} onClose={onClose} />
        <div class="mireporte-body"><div class="loading"><span class="spinner" /></div></div>
      </div>
    );
  }

  if (stage === 'error') {
    return (
      <div class="event-chat-screen">
        <Header eventTitle={eventTitle} eventId={eventId} onClose={onClose} />
        <div class="mireporte-body">
          <div class="mireporte-error">⚠️ {error || 'Algo no funcionó.'}</div>
          <button class="btn primary" onClick={onClose}>Volver</button>
        </div>
      </div>
    );
  }

  if (stage === 'done' && result) {
    const finalized = result.kind === 'finalized';
    return (
      <div class="event-chat-screen">
        <Header eventTitle={eventTitle} eventId={eventId} onClose={onClose} />
        <div class="mireporte-body">
          <div class="mireporte-success">
            <div class="mireporte-success-icon">✓</div>
            <h3>
              {finalized
                ? '¡Reporte recibido y evento cerrado!'
                : result.kind === 'edited'
                ? 'Reporte actualizado'
                : 'Reporte enviado'}
            </h3>
            <p class="mireporte-success-msg">
              {finalized
                ? 'Todos reportaron. Tu pago calculado llegará en breve.'
                : `Faltan ${result.remaining?.length || 0} reporte${result.remaining?.length === 1 ? '' : 's'} para que el capitán pueda cerrar.`}
            </p>
            {result.kind === 'edited' && result.oldVentas !== null && (
              <p class="mireporte-meta">
                Anterior: ventas RD$ {fmt(result.oldVentas)}, propinas RD$ {fmt(result.oldPropinas || 0)}<br />
                Esta era tu única corrección permitida.
              </p>
            )}
          </div>
          <button class="btn primary" onClick={onClose}>Listo</button>
        </div>
      </div>
    );
  }

  if (stage === 'conflict' && conflict) {
    return (
      <div class="event-chat-screen">
        <Header eventTitle={eventTitle} eventId={eventId} onClose={onClose} />
        <div class="mireporte-body">
          <div class="mireporte-conflict">
            <h3>Ya reportaste por {conflict.existing.source === 'group' ? 'el grupo' : 'la app'}</h3>
            <p class="mireporte-meta">
              Reporte actual:<br />
              <strong>ventas RD$ {fmt(conflict.existing.ventas)}, propinas RD$ {fmt(conflict.existing.propinas)}</strong>
            </p>
            <p class="mireporte-meta">
              Quieres reemplazarlo con:<br />
              <strong>ventas RD$ {fmt(conflict.attempted.ventas)}, propinas RD$ {fmt(conflict.attempted.propinas)}</strong>
            </p>
            {conflict.existing.edited && (
              <p class="mireporte-warning">
                ⚠️ Ya editaste este reporte una vez. Esta corrección requiere ayuda de Operaciones.
              </p>
            )}
            <div class="mireporte-actions">
              {!conflict.existing.edited && (
                <button class="btn primary" onClick={() => submit(true)}>Reemplazar</button>
              )}
              <button class="btn ghost" onClick={() => setStage('ready')}>Volver</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // stage === 'ready' or 'submitting'
  const closureBlocked = status?.state === 'not_initiated';
  return (
    <div class="event-chat-screen">
      <Header eventTitle={eventTitle} eventId={eventId} onClose={onClose} />
      <div class="mireporte-body">
        {closureBlocked ? (
          <div class="mireporte-blocked">
            <div class="mireporte-blocked-icon">⏳</div>
            <h3>El capitán aún no inició el cierre</h3>
            <p class="mireporte-meta">
              Cuando el evento termine, el capitán abrirá el cierre y entonces podrás registrar tus números aquí.
            </p>
            <p class="mireporte-meta" style={{ marginTop: 8 }}>
              También puedes reportar por el grupo de Telegram como siempre.
            </p>
          </div>
        ) : (
          <>
            <h3>Reporta tu venta y propina</h3>
            {status?.myReport && (
              <div class="mireporte-existing">
                Ya reportaste: ventas RD$ {fmt(status.myReport.ventas)}, propinas RD$ {fmt(status.myReport.propinas)}.
                Puedes corregir una sola vez.
              </div>
            )}
            <form onSubmit={(e) => { e.preventDefault(); submit(false); }}>
              <label class="mireporte-field">
                <span>Ventas DOP</span>
                <input
                  type="number" inputMode="numeric" min="0" step="1"
                  value={ventas}
                  onInput={(e) => setVentas((e.target as HTMLInputElement).value)}
                  placeholder="ej. 15000"
                  required
                />
              </label>
              <label class="mireporte-field">
                <span>Propinas DOP</span>
                <input
                  type="number" inputMode="numeric" min="0" step="1"
                  value={propinas}
                  onInput={(e) => setPropinas((e.target as HTMLInputElement).value)}
                  placeholder="ej. 2500"
                  required
                />
              </label>
              {error && <div class="mireporte-error">⚠️ {error}</div>}
              <button type="submit" class="btn primary" disabled={stage === 'submitting'}>
                {stage === 'submitting' ? 'Enviando...' : (status?.myReport ? 'Actualizar reporte' : 'Enviar reporte')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function Header({ eventId, eventTitle, onClose }: { eventId: string; eventTitle?: string; onClose: () => void }) {
  return (
    <header class="event-chat-header">
      <button class="event-chat-back" onClick={onClose} aria-label="Cerrar">←</button>
      <div class="event-chat-titles">
        <div class="event-chat-title">Mi reporte · {eventTitle || eventId}</div>
        <div class="event-chat-sub">Ventas y propinas del evento</div>
      </div>
    </header>
  );
}

function fmt(n: number): string {
  return n.toLocaleString('es-DO');
}
